// The correlation names of telemetry (CHT-770, Phase 13.4). They link a Sentry event or span, a Mastra trace, a structured log line and an
// operational diagnostic to the domain record they are about. Ids, versions, codes and numbers only: never a name, an email, a phone number,
// a message text, an amount or a place. The job names are the ones of `packages/application/src/job-correlation.ts` (CHT-761): this file
// repeats their key strings (the domain cannot import the application) and a parity test in the application fails when they differ.
//
// A correlation is a POINTER. It says where to look in Postgres. It is never proof of a payment, a request or a confirmation.
export const telemetryCorrelationKeys = Object.freeze([
    // The request and its correlation id (the API sets both).
    "lafayette.request_id",
    "lafayette.correlation_id",
    // The domain records.
    "lafayette.conversation_id",
    "lafayette.agent_run_id",
    "lafayette.concierge_request_id",
    "lafayette.payment_request_id",
    "lafayette.import_job_id",
    "lafayette.handoff_id",
    "lafayette.itinerary_id",
    // A provider event (a webhook delivery receipt) and the provider it came from.
    "lafayette.provider",
    "lafayette.provider_event_id",
    // The background run (`JobCorrelation` of CHT-761).
    "lafayette.intent_id",
    "lafayette.task_id",
    "lafayette.task_version",
    "lafayette.attempt",
    "lafayette.subject_type",
    "lafayette.subject_id",
    "lafayette.runner",
    "trigger.run_id",
]);
/** The keys that `JobCorrelation` (CHT-761) uses. A parity test compares them with `jobCorrelationOf`. */
export const JOB_CORRELATION_KEYS = Object.freeze([
    "lafayette.intent_id",
    "lafayette.task_id",
    "lafayette.task_version",
    "lafayette.attempt",
    "lafayette.subject_type",
    "lafayette.subject_id",
    "lafayette.correlation_id",
    "lafayette.runner",
    "trigger.run_id",
]);
const KNOWN = new Set(telemetryCorrelationKeys);
const ID_TOKEN = /^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/;
const MAX_VERSION = 1_000_000;
/**
 * The attributes to attach to a signal: only keys of the closed list, only id-shaped strings and whole non-negative numbers. A `null`, an
 * unknown key, a text with spaces, an email and a long value are dropped, so a caller cannot smuggle content through a correlation field.
 */
export function correlationAttributes(input) {
    const out = {};
    for (const [key, value] of Object.entries(input)) {
        if (!KNOWN.has(key))
            continue;
        if (typeof value === "number") {
            if (Number.isInteger(value) && value >= 0 && value <= MAX_VERSION)
                out[key] = value;
        }
        else if (typeof value === "string" && ID_TOKEN.test(value) && !value.includes("@")) {
            out[key] = value;
        }
    }
    return out;
}
/** The same attributes as string tags (Sentry tags are strings). */
export function correlationTags(input) {
    return Object.fromEntries(Object.entries(correlationAttributes(input)).map(([key, value]) => [key, String(value)]));
}
const UUID = "[0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12}";
// A route shape names the record it is about. Only the id segment is read, and only when it is a uuid.
const PATH_RULES = [
    [new RegExp(`/concierge-requests/(${UUID})(?:/|$)`), "lafayette.concierge_request_id"],
    [new RegExp(`/payment-requests/(${UUID})(?:/|$)`), "lafayette.payment_request_id"],
    [new RegExp(`/payments/(${UUID})(?:/|$)`), "lafayette.payment_request_id"],
    [new RegExp(`/imports/catalog/jobs/(${UUID})(?:/|$)`), "lafayette.import_job_id"],
    [new RegExp(`/conversations/(${UUID})(?:/|$)`), "lafayette.conversation_id"],
    [new RegExp(`/handoffs/(${UUID})(?:/|$)`), "lafayette.handoff_id"],
    [new RegExp(`/itineraries/(${UUID})(?:/|$)`), "lafayette.itinerary_id"],
    [new RegExp(`/operations/jobs/(${UUID})(?:/|$)`), "lafayette.intent_id"],
];
/** The domain ids that an API path names (lower case). A path with no known shape gives `{}`. Never reads a query string. */
export function correlationFromPath(path) {
    const clean = path.replace(/[?#].*$/, "");
    const out = {};
    for (const [pattern, key] of PATH_RULES) {
        const match = pattern.exec(clean);
        if (match?.[1] !== undefined)
            out[key] = match[1].toLowerCase();
    }
    return out;
}
/**
 * The correlation of an agent trace: the `lf.*` ids of the runtime (ADR 0002) under the shared names. Mastra traces keep their own allowlisted
 * `lf.*` keys; a log or a Sentry event of the same run carries these, so one search finds both.
 */
export function correlationFromAgentAttributes(attributes) {
    return correlationAttributes({
        "lafayette.agent_run_id": attributes["lf.run_id"],
        "lafayette.correlation_id": attributes["lf.correlation_id"],
        "lafayette.conversation_id": attributes["lf.conversation_id"],
        "lafayette.attempt": attributes["lf.attempt"],
        // The job correlation of a model run that a background job started (CHT-761 names).
        "lafayette.intent_id": attributes["lf.intent_id"],
        "lafayette.task_id": attributes["lf.task_id"],
        "lafayette.task_version": attributes["lf.task_version"],
        "lafayette.runner": attributes["lf.runner"],
        "trigger.run_id": attributes["lf.trigger_run_id"],
    });
}
